import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, Table, TableBody, TableCell, 
  TableHead, TableRow, Chip, Button, TextField, MenuItem, 
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert, Grid,
  Autocomplete, TablePagination, Tooltip, IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../api';

const AssignComponents = () => {
  const [assignments, setAssignments] = useState([]);
  const [masterComponents, setMasterComponents] = useState([]);
  const [prerequisites, setPrerequisites] = useState({ employees: [], departments: [] });
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, msg: '', type: 'success' });
  
  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  
  const currentMonth = new Date().toISOString().slice(0, 7);
  const initialForm = {
    target_type: 'Employee',
    target_value: null,
    category: 'Addition',
    sub_category: '',
    amount: '',
    application_type: 'Monthly',
    target_month: currentMonth,
    start_month: '',
    end_month: ''
  };
  const [formData, setFormData] = useState(initialForm);

  // Filters & Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assnRes, masterRes, prereqRes] = await Promise.all([
        api.get('/payroll/components/assignments'),
        api.get('/payroll/components/master'),
        api.get('/payroll/components/prerequisites')
      ]);
      setAssignments(assnRes.data || []);
      setMasterComponents((masterRes.data || []).filter(c => c.status === 'Active'));
      setPrerequisites(prereqRes.data || { employees: [], departments: [] });
    } catch (err) {
      showNotif('Failed to load data.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const showNotif = (msg, type = 'success') => {
    setNotification({ show: true, msg, type });
    setTimeout(() => setNotification({ show: false, msg: '', type: 'success' }), 5000);
  };

  const handleCreate = async () => {
    if (!formData.sub_category || !formData.amount) {
      showNotif('Please fill all mandatory fields.', 'error'); return;
    }
    if (formData.target_type !== 'Company' && !formData.target_value) {
      showNotif(`Please select a specific ${formData.target_type}.`, 'error'); return;
    }
    if (formData.application_type === 'Period' && (!formData.start_month || !formData.end_month)) {
      showNotif('Start and End dates are required for Period assignments.', 'error'); return;
    }
    if (formData.application_type === 'Monthly' && !formData.target_month) {
      showNotif('Target month is required for Monthly assignments.', 'error'); return;
    }

    try {
      setSubmitLoading(true);
      const payload = {
        target_type: formData.target_type,
        target_value: formData.target_type === 'Company' ? 'ALL' : formData.target_value.id,
        category: formData.category,
        sub_category: formData.sub_category,
        amount: parseFloat(formData.amount),
        application_type: formData.application_type,
        target_month: formData.application_type === 'Monthly' ? formData.target_month : null,
        start_month: formData.application_type === 'Period' ? formData.start_month : null,
        end_month: formData.application_type === 'Period' ? formData.end_month : null,
      };
      
      const res = await api.post('/payroll/components/assignments', payload);
      showNotif(res.data.message);
      setDialogOpen(false);
      setFormData(initialForm);
      fetchData();
    } catch (err) {
      showNotif(err.response?.data?.detail || 'Failed to assign component.', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
    try {
      await api.put(`/payroll/components/assignments/${id}`, { status: newStatus });
      showNotif(`Assignment marked as ${newStatus}.`);
      fetchData();
    } catch (err) { showNotif('Failed to update status.', 'error'); }
  };

  const handleExportCSV = () => {
    const headers = ['Employee Name', 'Employee ID', 'Category', 'Sub-Category', 'Amount', 'Type', 'Period', 'Status'];
    const csvContent = "data:text/csv;charset=utf-8," 
      + headers.join(",") + "\n"
      + filteredAssignments.map(a => {
          const period = a.application_type === 'Period' ? `${a.start_month} to ${a.end_month}` : (a.start_month || '--');
          return `"${a.emp_name}","${a.emp_id}","${a.category}","${a.sub_category}",${a.amount},"${a.application_type}","${period}","${a.status}"`;
        }).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Payroll_Assignments_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const availableSubCategories = masterComponents.filter(c => c.category === formData.category);

  const filteredAssignments = assignments.filter(a => {
    const matchSearch = (a.emp_name && a.emp_name.toLowerCase().includes(searchQuery.toLowerCase())) || 
                        (a.emp_id && a.emp_id.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchCat = filterCategory === 'All' || a.category === filterCategory;
    const matchStatus = filterStatus === 'All' || a.status === filterStatus;
    return matchSearch && matchCat && matchStatus;
  });

  const paginatedAssignments = filteredAssignments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {notification.show && <Alert severity={notification.type} sx={{ mb: 2 }}>{notification.msg}</Alert>}

      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>Component Assignments</Typography>
            <Typography variant="body2" color="textSecondary">Assign components by Employee, Department, or Company-wide.</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField size="small" label="Search Emp Name / ID" value={searchQuery} onChange={(e) => {setSearchQuery(e.target.value); setPage(0);}} sx={{ width: 220 }} />
            <TextField select size="small" label="Category" value={filterCategory} onChange={(e) => {setFilterCategory(e.target.value); setPage(0);}} sx={{ width: 120 }}>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Addition">Addition</MenuItem>
              <MenuItem value="Deduction">Deduction</MenuItem>
            </TextField>
            <TextField select size="small" label="Status" value={filterStatus} onChange={(e) => {setFilterStatus(e.target.value); setPage(0);}} sx={{ width: 120 }}>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </TextField>
            <Tooltip title="Export to CSV">
              <IconButton color="primary" onClick={handleExportCSV} sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>Assign</Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
        ) : (
          <>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Component</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type & Period</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedAssignments.map((asn) => (
                  <TableRow key={asn.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{asn.emp_name}</Typography>
                      <Typography variant="caption" color="textSecondary">{asn.emp_id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={asn.category} size="small" color={asn.category === 'Addition' ? 'success' : 'error'} variant="outlined" sx={{ mb: 0.5 }} />
                      <Typography variant="body2" display="block">{asn.sub_category}</Typography>
                    </TableCell>
                    <TableCell><Typography sx={{ fontWeight: 'bold' }}>{asn.amount.toFixed(2)}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2">{asn.application_type}</Typography>
                      {asn.application_type === 'Period' ? (
                        <Typography variant="caption" color="textSecondary">{asn.start_month} to {asn.end_month}</Typography>
                      ) : (
                        <Typography variant="caption" color="textSecondary">Month: {asn.start_month?.substring(0,7) || '--'}</Typography>
                      )}
                    </TableCell>
                    <TableCell><Chip label={asn.status} size="small" color={asn.status === 'Active' ? 'primary' : 'default'} /></TableCell>
                    <TableCell align="center">
                      <Button size="small" variant="outlined" color={asn.status === 'Active' ? 'warning' : 'success'} onClick={() => toggleStatus(asn.id, asn.status)}>
                        {asn.status === 'Active' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {assignments.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ p: 4 }}>No assignments found.</TableCell></TableRow>}
              </TableBody>
            </Table>
            <TablePagination
              component="div" count={filteredAssignments.length} page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </Paper>

      {/* ASSIGNMENT DIALOG - CHANGED TO sm */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Assign Salary Component</DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          
          <Box sx={{ mt: 1, mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '1px solid #eee', pb: 0.5 }}>WHO (Target Scope)</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={5}>
                <TextField select fullWidth label="Target Scope" value={formData.target_type} onChange={(e) => setFormData({ ...formData, target_type: e.target.value, target_value: null })}>
                  <MenuItem value="Employee">Individual Employee</MenuItem>
                  <MenuItem value="Department">Entire Department</MenuItem>
                  <MenuItem value="Company">Company-Wide</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={7}>
                {formData.target_type === 'Employee' && (
                  <Autocomplete
                    options={prerequisites.employees}
                    getOptionLabel={(option) => `${option.name} (${option.id})`}
                    onChange={(event, newValue) => setFormData({ ...formData, target_value: newValue })}
                    renderInput={(params) => <TextField {...params} label="Search Employee Name / ID" fullWidth />}
                  />
                )}
                {formData.target_type === 'Department' && (
                  <Autocomplete
                    options={prerequisites.departments}
                    getOptionLabel={(option) => option.name}
                    onChange={(event, newValue) => setFormData({ ...formData, target_value: newValue })}
                    renderInput={(params) => <TextField {...params} label="Select Department" fullWidth />}
                  />
                )}
                {formData.target_type === 'Company' && (
                  <TextField fullWidth disabled value="Applies to ALL Employees" />
                )}
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '1px solid #eee', pb: 0.5 }}>WHAT (Component Details)</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Category" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value, sub_category: '' })}>
                  <MenuItem value="Addition">Addition</MenuItem>
                  <MenuItem value="Deduction">Deduction</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField 
                  select fullWidth label="Select Sub-Category" 
                  value={formData.sub_category} 
                  onChange={(e) => setFormData({ ...formData, sub_category: e.target.value })}
                >
                  {availableSubCategories.length === 0 && <MenuItem disabled value="">No active components</MenuItem>}
                  {availableSubCategories.map(c => <MenuItem key={c.id} value={c.sub_category}>{c.sub_category}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Amount" type="number" placeholder="0.00" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '1px solid #eee', pb: 0.5 }}>WHEN (Duration)</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Application Type" value={formData.application_type} onChange={(e) => setFormData({ ...formData, application_type: e.target.value })}>
                  <MenuItem value="Monthly">One-Time (Monthly)</MenuItem>
                  <MenuItem value="Period">Recurring (Period)</MenuItem>
                </TextField>
              </Grid>

              {formData.application_type === 'Period' ? (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={formData.start_month} onChange={(e) => setFormData({ ...formData, start_month: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth type="date" label="End Date" InputLabelProps={{ shrink: true }} value={formData.end_month} onChange={(e) => setFormData({ ...formData, end_month: e.target.value })} />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} sm={8}>
                  <TextField fullWidth type="month" label="Target Month" InputLabelProps={{ shrink: true }} value={formData.target_month} onChange={(e) => setFormData({ ...formData, target_month: e.target.value })} />
                </Grid>
              )}
            </Grid>
          </Box>

        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitLoading}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={submitLoading}>
            {submitLoading ? <CircularProgress size={24} color="inherit" /> : 'Confirm Assignment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssignComponents;